import { useSecurityOverview } from '@/hooks/use-products';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const CHART_TOOLTIP_STYLE = {
  contentStyle: { background: '#161616', border: '1px solid #1E1E1E', borderRadius: 6, fontSize: '0.78rem' },
  labelStyle: { color: '#A0A0A0' },
};
const CHART_GRID = { strokeDasharray: '3 3', stroke: 'rgba(255,255,255,0.04)' };
const AXIS_TICK = { fill: '#A0A0A0', fontSize: 11 };

export function SecurityOverviewPage() {
  const { data: secData, isLoading } = useSecurityOverview();

  if (isLoading) {
    return (
      <div>
        <div className="mb-8">
          <div className="h-7 w-48 bg-bg-elevated rounded animate-pulse mb-1" />
          <div className="h-4 w-72 bg-bg-elevated rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-4 mb-8">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="bg-bg-card border border-border rounded-md p-4 h-20 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!secData) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="font-display text-2xl font-bold tracking-tight">Security Posture</h1>
          <p className="text-text-secondary text-[0.82rem] mt-1">Vulnerability and compliance status across the ecosystem</p>
        </div>
        <div className="text-center py-16 text-text-muted">
          <div className="text-2xl mb-3 opacity-40">&#9632;</div>
          <h3 className="text-base text-text-secondary mb-1">No security data yet</h3>
          <p className="text-[0.78rem] max-w-[400px] mx-auto">
            Security posture is aggregated from security scans. Start reporting via the ingest API.
          </p>
        </div>
      </div>
    );
  }

  const { summary, by_product, vuln_trend } = secData;

  const trendChartData = vuln_trend.map(v => ({
    date: new Date(v.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    'Prod Critical': v.critical,
    'Prod High': v.high_prod,
    'Dev High': v.high_dev,
  }));

  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-2xl font-bold tracking-tight">Security Posture</h1>
        <p className="text-text-secondary text-[0.82rem] mt-1">Vulnerability and compliance status across the ecosystem</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-4 mb-8">
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${summary.prod_critical === 0 ? 'text-success' : 'text-danger'}`}>
            {summary.prod_critical}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">Critical (Prod)</div>
        </div>
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${summary.prod_high === 0 ? 'text-success' : 'text-danger'}`}>
            {summary.prod_high}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">High (Prod)</div>
        </div>
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${summary.dev_high === 0 ? 'text-success' : 'text-warning'}`}>
            {summary.dev_high}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">High (Dev)</div>
        </div>
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${summary.secrets_exposed === 0 ? 'text-success' : 'text-danger'}`}>
            {summary.secrets_exposed}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">Secrets Exposed</div>
        </div>
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${summary.owasp_passed === summary.owasp_total && summary.owasp_total > 0 ? 'text-success' : 'text-text-muted'}`}>
            {summary.owasp_total > 0 ? `${summary.owasp_passed}/${summary.owasp_total}` : '—'}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">OWASP Checks</div>
        </div>
      </div>

      {/* Vulnerability Trend Chart */}
      {trendChartData.length > 0 && (
        <div className="bg-bg-card border border-border rounded-lg p-5 mb-6">
          <h4 className="text-[0.85rem] font-semibold mb-4">Vulnerability Trend</h4>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendChartData}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="date" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: '0.72rem' }} iconSize={12} />
                <Line type="monotone" dataKey="Prod Critical" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Prod High" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Dev High" stroke="#6A6A6A" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Per-Product Table */}
      <div className="bg-bg-card border border-border rounded-lg p-5">
        <h4 className="text-[0.85rem] font-semibold mb-4">Security by Product</h4>
        <table className="w-full border-collapse text-[0.78rem]">
          <thead>
            <tr>
              {['Product', 'Prod Critical', 'Prod High', 'OWASP', 'Secrets', 'ESLint Security', 'Score'].map(h => (
                <th key={h} className="text-left text-[0.62rem] font-semibold uppercase tracking-wider text-text-muted px-3 py-2.5 border-b border-border">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {by_product.map(row => {
              const s = row.scan as Record<string, number> | null;
              if (!s) {
                return (
                  <tr key={row.product.slug} className="hover:bg-bg-elevated">
                    <td className="px-3 py-2.5 border-b border-border-subtle">{row.product.name}</td>
                    <td className="px-3 py-2.5 border-b border-border-subtle text-text-muted" colSpan={6}>No scan data</td>
                  </tr>
                );
              }
              const prodCrit = s.npm_prod_critical ?? 0;
              const prodHigh = s.npm_prod_high ?? 0;
              const owaspPassed = s.owasp_passed ?? 0;
              const owaspTotal = s.owasp_total ?? 0;
              const secrets = s.gitleaks_findings ?? 0;
              const eslintErr = s.eslint_errors ?? 0;
              const secScore = owaspTotal > 0 && prodCrit === 0 && prodHigh === 0 && secrets === 0 && eslintErr === 0
                ? 100
                : owaspTotal > 0
                  ? Math.round((owaspPassed / owaspTotal) * 100 * (prodCrit === 0 ? 1 : 0.5) * (prodHigh === 0 ? 1 : 0.8))
                  : 0;

              return (
                <tr key={row.product.slug} className="hover:bg-bg-elevated">
                  <td className="px-3 py-2.5 border-b border-border-subtle font-semibold">{row.product.name}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${prodCrit === 0 ? 'text-success' : 'text-danger'}`}>{prodCrit}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${prodHigh === 0 ? 'text-success' : 'text-danger'}`}>{prodHigh}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${owaspPassed === owaspTotal && owaspTotal > 0 ? 'text-success' : 'text-text-muted'}`}>
                    {owaspTotal > 0 ? `${owaspPassed}/${owaspTotal}` : '—'}
                  </td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${secrets === 0 ? 'text-success' : 'text-danger'}`}>{secrets}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${eslintErr === 0 ? 'text-success' : 'text-danger'}`}>{eslintErr}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${secScore === 100 ? 'text-success' : secScore >= 80 ? 'text-warning' : 'text-danger'}`}>
                    {secScore}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
